import { useEffect, useRef } from "react";
import {
  PropertyViewed,
  TotalTimeSpent,
  trackPageView
} from "../stories/investor/amplitude";
import { useLocation, useSearchParams } from "react-router-dom";
import useContextHook from "../hooks/context/useContext";

interface PageTrackerProps {
  pageName: string;
  objPropertyDetails?: {
    id?: string;
    opportunitySFID?: string;
    pubPropertySFID?: string;
    propertySFID?: string;
  };
  shareId: string;
  propertyId: string | undefined;
}

const PageTracker: React.FC<PageTrackerProps> = ({
  pageName,
  objPropertyDetails,
  shareId,
  propertyId
}) => {
  const pageEnterTime = useRef(Date.now());
  const hasTrackedTime = useRef(false);
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const cid = searchParams.get("cid");
  const eq = searchParams.get("eq") || "";
  const { mobileNumber } = useContextHook();
  const mobileNumber1 = localStorage.getItem("mobileNumber") || "";
  const handlePageExit = () => {
    // Only track time if we haven't already tracked it
    if (!hasTrackedTime.current) {
      const timeSpent = (Date.now() - pageEnterTime.current) / 1000;

      // Only track if we have valid property details
      if (objPropertyDetails?.id) {
        TotalTimeSpent({
          timeSpent,
          propertyId: objPropertyDetails.id,
          opportunitySFID: objPropertyDetails.opportunitySFID || "",
          pubPropertySFID: objPropertyDetails.pubPropertySFID || "",
          propertySFID: objPropertyDetails.propertySFID || "",
          shareId,
          linkCId: cid || "",
          linkEmailId: eq || "",
          mobileNumber: mobileNumber || mobileNumber1
        });
        hasTrackedTime.current = true;
      }
    }
  };

  useEffect(() => {
    // Reset tracking state when component mounts
    hasTrackedTime.current = false;
    pageEnterTime.current = Date.now();

    // Track page view for investor pages
    trackPageView({
      pathname: location.pathname,
      linkCId: cid || "",
      linkEmailId: eq || ""
    });

    // Track property view if we have valid property details
    if (
      objPropertyDetails?.propertySFID &&
      objPropertyDetails?.id &&
      objPropertyDetails?.pubPropertySFID &&
      objPropertyDetails?.opportunitySFID
    ) {
      PropertyViewed({
        propertySFID: objPropertyDetails?.propertySFID || "",
        propertyId: propertyId || "",
        shareId: shareId,
        pubPropertySFID: objPropertyDetails?.pubPropertySFID || "",
        opportunitySFID: objPropertyDetails?.opportunitySFID || "",
        linkCId: cid || "",
        linkEmailId: eq || "",
        mobileNumber: mobileNumber || mobileNumber1
      });
    }

    // Handle page unload
    const handleBeforeUnload = () => {
      handlePageExit();
    };

    // Handle visibility change
    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        handlePageExit();
      }
    };

    // Add event listeners
    window.addEventListener("beforeunload", handleBeforeUnload);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    // Cleanup function
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [pageName, objPropertyDetails, shareId, propertyId, location.pathname]);

  return null;
};

export default PageTracker;
